import React, { useMemo, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  FormControl,
  Select,
  MenuItem,
  Pagination
} from '@mui/material';
import { LineChart, BarChart } from '@mui/x-charts';

// ---------------- FORMATADOR ----------------
const formatarMoeda = (valor: number) => {
  return new Intl.NumberFormat('pt-BR', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(valor);
};

// ---------------- MOCK DATA ----------------
const gerarVendasMes = () => {
  const hoje = new Date();

  return Array.from({ length: 12 }, (_, i) => {
    const data = new Date(hoje.getFullYear(), hoje.getMonth() - (11 - i), 1);

    const label = data.toLocaleDateString('pt-BR', { month: 'short' });

    return {
      label: label.charAt(0).toUpperCase() + label.slice(1),
      value: Math.floor(Math.random() * 10000) + 1000
    };
  });
};

const gerarVendasDia = () =>
  Array.from({ length: 30 }, (_, i) => ({
    label: `${i + 1}`,
    value: Math.floor(Math.random() * 1000) + 100
  }));

const gerarProdutos = () =>
  Array.from({ length: 100 }, (_, i) => ({
    label: `Produto ${i + 1}`,
    value: Math.floor(Math.random() * 5000)
  })).sort((a, b) => b.value - a.value);

const gerarFabricas = () =>
  Array.from({ length: 50 }, (_, i) => ({
    label: `Fábrica ${i + 1}`,
    value: Math.floor(Math.random() * 8000)
  })).sort((a, b) => b.value - a.value);

const gerarClientes = () =>
  Array.from({ length: 100 }, (_, i) => ({
    label: `Cliente ${i + 1}`,
    value: Math.floor(Math.random() * 20000)
  })).sort((a, b) => b.value - a.value);

// ---------------- COMPONENT ----------------
export const DashboardVendas: React.FC = () => {
  const [tipoPeriodo, setTipoPeriodo] = useState<'mes' | 'dia'>('mes');
  const [paginaProdutos, setPaginaProdutos] = useState(1);
  const [paginaClientes, setPaginaClientes] = useState(1);
  const [tipoRanking, setTipoRanking] = useState<'produto' | 'fabrica'>('produto');

  const itensPorPagina = 20;

  const vendas = useMemo(() => {
    return tipoPeriodo === 'mes' ? gerarVendasMes() : gerarVendasDia();
  }, [tipoPeriodo]);

  const produtos = useMemo(() => gerarProdutos(), []);
  const fabricas = useMemo(() => gerarFabricas(), []);
  const clientes = useMemo(() => gerarClientes(), []);

  const listaAtual = tipoRanking === 'produto' ? produtos : fabricas;

  const produtosPaginados = useMemo(() => {
    const inicio = (paginaProdutos - 1) * itensPorPagina;
    return listaAtual.slice(inicio, inicio + itensPorPagina);
  }, [paginaProdutos, listaAtual]);

  const clientesPaginados = useMemo(() => {
    const inicio = (paginaClientes - 1) * itensPorPagina;
    return clientes.slice(inicio, inicio + itensPorPagina);
  }, [paginaClientes, clientes]);

  const totalPaginasProdutos = Math.ceil(listaAtual.length / itensPorPagina);
  const totalPaginasClientes = Math.ceil(clientes.length / itensPorPagina);

  return (
    <Box
      p={2}
      sx={{
        minHeight: '100vh',
        background: '#f4f6f8'
      }}
    >
      <Grid container spacing={2}>

        {/* ================= VENDAS ================= */}
        <Grid item xs={12}>
          <Paper sx={{ p: 2, borderRadius: 3 }}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Vendas</Typography>

              <FormControl size="small">
                <Select
                  value={tipoPeriodo}
                  onChange={(e) => setTipoPeriodo(e.target.value as any)}
                >
                  <MenuItem value="mes">Por Mês</MenuItem>
                  <MenuItem value="dia">Por Dia</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <LineChart
              height={300}
              series={[
                {
                  data: vendas.map(v => v.value),
                  area: true,
                  color: '#2563eb',
                  valueFormatter: (v) => formatarMoeda(v as number)
                }
              ]}
              xAxis={[{ scaleType: 'band', data: vendas.map(v => v.label) }]}
              yAxis={[{ valueFormatter: (v) => formatarMoeda(v as number) }]}
            >
              <defs>

                <linearGradient id="gradientArea" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#1e3a8a" stopOpacity="1" />     {/* azul bem escuro */}
                  <stop offset="50%" stopColor="#1d4ed8" stopOpacity="0.85" /> {/* azul forte */}
                  <stop offset="100%" stopColor="#06b6d4" stopOpacity="0.7" /> {/* ciano mais presente */}
                </linearGradient>
              </defs>

            </LineChart>
          </Paper>
        </Grid>

        {/* ================= PRODUTOS / FABRICAS ================= */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 420 }}>
            <Box display="flex" justifyContent="space-between" mb={2}>
              <Typography variant="h6">Ranking</Typography>

              <FormControl size="small">
                <Select
                  value={tipoRanking}
                  onChange={(e) => {
                    setTipoRanking(e.target.value as any);
                    setPaginaProdutos(1);
                  }}
                >
                  <MenuItem value="produto">Produtos</MenuItem>
                  <MenuItem value="fabrica">Fábricas</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <BarChart
              height={260}
              series={[
                {
                  data: produtosPaginados.map(p => p.value),
                  color: 'rgba(37, 99, 235, 0.6)',
                  valueFormatter: (v) => formatarMoeda(v as number)
                }
              ]}
              xAxis={[{ scaleType: 'band', data: produtosPaginados.map(p => p.label) }]}
              yAxis={[{ valueFormatter: (v) => formatarMoeda(v as number) }]}
            />

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPaginasProdutos}
                page={paginaProdutos}
                onChange={(_, value) => setPaginaProdutos(value)}
              />
            </Box>
          </Paper>
        </Grid>

        {/* ================= CLIENTES ================= */}
        <Grid item xs={12} md={6}>
          <Paper sx={{ p: 2, borderRadius: 3, height: 420 }}>
            <Typography variant="h6" mb={2}>
              Clientes que Mais Compram
            </Typography>

            <BarChart
              height={260}
              series={[
                {
                  data: clientesPaginados.map(c => c.value),
                  color: 'rgba(34, 211, 238, 0.6)',
                  valueFormatter: (v) => formatarMoeda(v as number)
                }
              ]}
              xAxis={[{ scaleType: 'band', data: clientesPaginados.map(c => c.label) }]}
              yAxis={[{ valueFormatter: (v) => formatarMoeda(v as number) }]}
            />

            <Box display="flex" justifyContent="center" mt={2}>
              <Pagination
                count={totalPaginasClientes}
                page={paginaClientes}
                onChange={(_, value) => setPaginaClientes(value)}
              />
            </Box>
          </Paper>
        </Grid>

      </Grid>
    </Box>
  );
};